/*!
 * dropzone v0.10.0 
 * (c) 2020 Alex Tkachenko
 * Released under the MIT License.
 */
import { T, partition } from 'ramda';
import EventEmitter from '@aspectus/event-emitter';

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

var toArray = function toArray(iterable) {
  return Array.prototype.slice.call(iterable);
};

function filesResolver(event) {
  var files = [];

  if (event.dataTransfer.files.length > 0) {
    files = toArray(event.dataTransfer.files);
  } else if (event.dataTransfer.items) {
    // FIXME
    // Method `.webkitGetAsEntry` is returning other type of file, so
    // it must be converted to a corresponding object type.
    files = toArray(event.dataTransfer.items).map(function (item) {
      return item.webkitGetAsEntry();
    });
  }

  return files;
}
var Dropzone =
/*#__PURE__*/
function () {
  function Dropzone() {
    var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    _classCallCheck(this, Dropzone);

    /**
     * Dragging counter.
     *
     * @memberof DropZone
     */
    this.dragging = 0;
    /**
     * Responsible for DropZone event emitting.
     *
     * @memberof DropZone
     */

    this.emitter = new EventEmitter();
    /**
     * Elements, that are used as drop zones.
     *
     * @memberof DropZone
     */

    this.zones = [];
    /**
     * Input elements, to handle file selection.
     *
     * @memberof DropZone
     */

    this.inputs = [];
    this.config = {
      resolver: filesResolver,
      acceptor: T
    };
    this.configure(config);
    this.onDragover = this.onDragover.bind(this);
    this.onDragenter = this.onDragenter.bind(this);
    this.onDragleave = this.onDragleave.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.onSelect = this.onSelect.bind(this);
  }

  _createClass(Dropzone, [{
    key: "configure",
    value: function configure() {
      var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var resolver = config && config.resolver || this.config.resolver;
      var acceptor = config && config.acceptor || this.config.acceptor;
      this.config = {
        resolver: resolver,
        acceptor: acceptor
      };
      this.resolver = resolver;
      this.acceptor = acceptor;
      this.partitioner = partition(this.acceptor);
    }
    /**
     * Binds DropZone to a particular elements.
     *
     * @param {*} zones Array of DOM elements that will be used as drop zones.
     * @param {*} inputs Array of DOM elements that will be used as file inputs.
     * @memberof DropZone
     */

  }, {
    key: "bind",
    value: function bind(zones, inputs) {
      var _this = this;

      this.zones = zones.concat(this.zones);
      this.inputs = inputs.concat(this.inputs);
      this.zones.forEach(function (element) {
        element.addEventListener('dragover', _this.onDragover, false);
        element.addEventListener('dragenter', _this.onDragenter, false);
        element.addEventListener('dragleave', _this.onDragleave, false);
        element.addEventListener('drop', _this.onDrop, false);
      });
      this.inputs.forEach(function (element) {
        element.addEventListener('change', _this.onSelect);
      });
    }
    /**
     * Unbinds all listeners.
     *
     * @memberof DropZone
     */

  }, {
    key: "unbind",
    value: function unbind() {
      var _this2 = this;

      this.zones.forEach(function (element) {
        element.removeEventListener('dragover', _this2.onDragover, false);
        element.removeEventListener('dragenter', _this2.onDragenter, false);
        element.removeEventListener('dragleave', _this2.onDragleave, false);
        element.removeEventListener('drop', _this2.onDrop, false);
      });
      this.inputs.forEach(function (element) {
        element.removeEventListener('change', _this2.onSelect);
      });
      delete this.emitter;
      delete this.zones;
      delete this.inputs;
    }
    /**
     * @param  {Event} event
     */

  }, {
    key: "onSelect",
    value: function onSelect(event) {
      // HTML file inputs do not seem to support folders, so assume this
      // is a flat file list.
      var files = toArray(event.target.files);

      var _this$partitioner = this.partitioner(files),
          _this$partitioner2 = _slicedToArray(_this$partitioner, 2),
          accepted = _this$partitioner2[0],
          rejected = _this$partitioner2[1];

      this.emitter.emit('drop', {
        event: event,
        accepted: accepted,
        rejected: rejected
      }); // Clearing out file input to be able to upload duplicates(for whatever reason).

      event.target.value = '';
    }
  }, {
    key: "onDragover",
    value: function onDragover(event) {
      event.stopPropagation();
      event.preventDefault(); // Makes it possible to drag files from chrome's download bar
      // http://stackoverflow.com/questions/19526430/drag-and-drop-file-uploads-from-chrome-downloads-bar
      // Try is required to prevent bug in Internet Explorer 11 (SCRIPT65535 exception)

      var effect;

      try {
        effect = event.dataTransfer.effectAllowed;
      } catch (error) {} // eslint-disable-line no-empty


      event.dataTransfer.dropEffect = effect === 'move' || effect === 'linkMove' ? 'move' : 'copy';
    }
    /**
     * @param {Event} event Event to handle.
     */

  }, {
    key: "onDragenter",
    value: function onDragenter(event) {
      event.stopPropagation();
      event.preventDefault();
      this.dragStart(event);
    }
    /**
     * @param {Event} event Event to handle.
     */

  }, {
    key: "onDragleave",
    value: function onDragleave(event) {
      event.stopPropagation();
      event.preventDefault();
      this.dragStop(event);
    }
  }, {
    key: "dragStart",
    value: function dragStart(event) {
      if (this.dragging === 0) {
        this.emitter.emit('drag-start', {
          event: event
        });
      }

      this.dragging++;
    }
  }, {
    key: "dragStop",
    value: function dragStop(event) {
      var force = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      this.dragging = force ? 0 : this.dragging - 1;

      if (this.dragging === 0) {
        this.emitter.emit('drag-stop', {
          event: event
        });
      }
    }
    /**
     * @param {Event} event Drop event.
     */

  }, {
    key: "onDrop",
    value: function onDrop(event) {
      event.stopPropagation();
      event.preventDefault();
      var files = this.resolver(event);

      var _this$partitioner3 = this.partitioner(files),
          _this$partitioner4 = _slicedToArray(_this$partitioner3, 2),
          accepted = _this$partitioner4[0],
          rejected = _this$partitioner4[1];

      this.dragStop(event, true);
      this.emitter.emit('drop', {
        event: event,
        accepted: accepted,
        rejected: rejected
      });
    }
  }]);

  return Dropzone;
}();

export { Dropzone, filesResolver };
